import useAPI from "@toothfairy/shared-api/useApi";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import envConfig from "../../envConfig";
import appLogHelper from "../API/AppLog/appLogHelper";
import trainingAPI from "../API/Training/trainingAPI";
import useWorkspaces from "../Hooks/useWorkspaces";

const ALL_ACCEPT_EXTENSIONS = [
  ".pdf",
  ".txt",
  ".md",
  ".html",
  ".docx",
  ".ppt",
  ".pptx",
  ".csv",
  ".xlsx",
  ".xlsm",
  ".java",
  ".py",
  ".yaml",
  ".yml",
  ".xml",
  ".sql",
  ".sh",
  ".php",
  ".js",
  ".ts",
  ".tsx",
  ".jsx",
  ".csharp",
  ".rb",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".svg",
  ".avif",
  ".mp3",
  ".m4a",
  ".aac",
  ".flac",
  ".ogg",
  ".mp4",
  ".webm",
  ".mov",
];

const useFileSelector = ({
  _importType,
  _importFile,
  additionalFilenameParams,
  _appLogImportType,
  _appLogImportEvent,
  showSuccessOnUpload = false,
  flat = true,
  createDocument = false,
  contentType,
  uploadOnSelection = true,
  onFileUploaded = () => {},
  onDocumentCreated = () => {},
  allowMultiple = false,
  allowFolderUpload = false,
  showPreview = false,
  onShowPreview = () => {},
  onDocumentsCreated = () => {},
  onFilePathChange = () => {},
  topics = [],
  allowAllTypes = false,
}) => {
  const { getActiveWorkspace } = useWorkspaces();
  const maxDocs = 50;
  const fileSizeConstant = 20;
  const trainingDataFileSizeConstant = 200;
  const FILE_SIZE_LIMITS = {
    pdf: fileSizeConstant * 1024 * 1024, // 25MB for PDFs
    generic: fileSizeConstant * 1024 * 1024, // 10MB for generic files
    gen_ai_training:
      trainingDataFileSizeConstant * 1024 * 1024, // 200MB for AI training files
    benchmark_ai: 1 * 1024 * 1024, // 2MB for benchmark CSV files
    artifact: fileSizeConstant * 1024 * 1024, // 20MB for artifact files
    default: fileSizeConstant * 1024 * 1024, // 10MB default limit
    "imported-files": fileSizeConstant * 1024 * 1024, // 12.5MB for imported files
    "imported-artifacts": fileSizeConstant * 1024 * 1024, // 20MB for imported artifacts
  };

  const [batchUploadStatus, setBatchUploadStatus] = useState({}); // This is just an example. Replace this with your actual state update logic.
  const {
    data: createAPIResult,
    error: createAPIError,
    loading: createAPIInProgress,
    apiRequest: createAPIRequest,
    lastUpdate: lastCreationTime,
  } = useAPI(trainingAPI.postTrainingData, envConfig);
  const {
    apiRequest: updateDocumentFieldsRequest,
  } = useAPI(trainingAPI.updateTrainingData, envConfig);
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [isFileUploadInProgress, setIsFileUploadInProgress] = useState(false);
  const [fileUploadProgress, SetFileUploadProgress] = useState(0);
  const [importType, setImportType] = useState(_importType);
  const [importFile, setImportFile] = useState(_importFile);
  const [filePath, setFilePath] = useState(null);
  const [isImportInProgress, setIsImportInProgress] = useState(false);
  const [fileSelector, SetFileSelector] = useState(null);
  const { t } = useTranslation();
  const { user } = AppUser.useUser();
  const AppToasty = AppToast.useToast();
  const _appLogsInImport = useMemo(() => {
    return state?.appLogs?.filter(
      (u) =>
        u.type === _appLogImportType &&
        (u.status === "dispatched" || u.status === "inProgress"),
    );
  }, [_appLogImportType, state.appLogs]);

  const {
    data: S3uploadUrlData,
    loading: urlUploadGenerationInProgress,
    apiRequest: S3uploadUrlRequest,
    response: S3uploadResponse,
  } = useAPI(trainingAPI.bulkImportUrlGeneration, envConfig);
  const validateBenchmarkCSV = (csvString) => {
    const MAX_FIELD_LENGTH = 6000;
    const errors = [];

    try {
      // Skip empty strings
      if (!csvString || csvString.trim() === "") {
        throw new Error("Empty file content");
      }

      // Split the string by newlines and filter out empty lines
      const lines = csvString.split("\n").filter((row) => row.trim() !== "");

      lines.forEach((line, index) => {
        const lineNumber = index + 1;
        const fields = line.split(";").map((field) => field.trim());

        // Validate number of fields (2 required + 1 optional)
        if (fields.length < 2 || fields.length > 4) {
          errors.push(
            `Line ${lineNumber}: Invalid number of fields. Expected 2-4 fields (question;answer;reasoning; context)`,
          );
          return;
        }

        const [question, answer, reasoning, context] = fields;

        // Validate required fields presence
        if (!question || question.trim() === "") {
          errors.push(`Line ${lineNumber}: Question field is required`);
        }

        if (!answer || answer.trim() === "") {
          errors.push(`Line ${lineNumber}: Answer field is required`);
        }

        // Validate field lengths
        if (question.length > MAX_FIELD_LENGTH) {
          errors.push(
            `Line ${lineNumber}: Question exceeds ${MAX_FIELD_LENGTH} characters`,
          );
        }

        if (answer.length > MAX_FIELD_LENGTH) {
          errors.push(
            `Line ${lineNumber}: Answer exceeds ${MAX_FIELD_LENGTH} characters`,
          );
        }

        if (reasoning && reasoning.length > MAX_FIELD_LENGTH) {
          errors.push(
            `Line ${lineNumber}: Reasoning exceeds ${MAX_FIELD_LENGTH} characters`,
          );
        }

        if (context && context.length > MAX_FIELD_LENGTH) {
          errors.push(
            `Line ${lineNumber}: Context exceeds ${MAX_FIELD_LENGTH} characters`,
          );
        }
      });

      return {
        isValid: errors.length === 0,
        errors,
      };
    } catch (error) {
      return {
        isValid: false,
        errors: [`File parsing error: ${error.message}`],
      };
    }
  };

  const validateFileSize = (file, importType) => {
    const errors = [];

    // Check if file exists and has size
    if (!file || !file.size) {
      errors.push("Invalid file or file size");
      return { isValid: false, errors };
    }

    const sizeLimit = FILE_SIZE_LIMITS[importType] || FILE_SIZE_LIMITS.default;
    if (file.size > sizeLimit) {
      const limitMB = Math.round(sizeLimit / (1024 * 1024));
      errors.push(
        `File "${file.name}" exceeds the maximum allowed size of ${limitMB}MB. Upgrade to increase limit.`,
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const validateBatchSize = (files, importType) => {
    const errors = [];

    if (!files || files.length === 0) {
      return { isValid: true, errors: [] };
    }

    // Calculate total size
    let totalSize = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file && file.size) {
        totalSize += file.size;
      }
    }

    const sizeLimit = FILE_SIZE_LIMITS[importType] || FILE_SIZE_LIMITS.default;
    if (totalSize > sizeLimit) {
      const maxSizeMB = Math.round(sizeLimit / (1024 * 1024));
      const currentSizeMB = Math.round(totalSize / (1024 * 1024));
      errors.push(
        `Total size of ${currentSizeMB}MB exceeds maximum allowed ${maxSizeMB}MB`,
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const importFileFromHook = (
    file,
    title,
    folderid,
    type,
    importFile = _importFile,
    contentType = contentType,
  ) => {
    setIsFileUploadInProgress(true);
    handleImport({
      file,
      importType,
      importFile,
      additionalFilenameParams,
      _appLogImportType,
      _appLogImportEvent,
      showSuccessOnUpload,
      flat,
      createDocument,
      contentType,
      title,
      folderid,
      type,
    });
  };

  const handleImports = async (
    type,
    folderid,
    status = "draft",
    topics = null,
    contentType = contentType,
    importType = _importType,
    scope = null,
    source = null,
    categorizedFiles = null,
    extractImages = false,
    advEmb = false,
  ) => {
    setIsFileUploadInProgress(true);

    if (categorizedFiles && allowAllTypes) {
      const { pdfFiles, genericFiles, artifactFiles } = categorizedFiles;
      const allDocuments = [];

      if (pdfFiles && pdfFiles.length > 0) {
        const pdfDocs = await processFiles(
          pdfFiles,
          "readComprehensionPdf",
          folderid,
          status,
          topics,
          "imported-pdf",
          "application/pdf",
          extractImages,
          advEmb,
        );
        allDocuments.push(...pdfDocs);
      }

      if (genericFiles && genericFiles.length > 0) {
        const genericDocs = await processFiles(
          genericFiles,
          "readComprehensionFile",
          folderid,
          status,
          topics,
          "imported-files",
          "generic",
          extractImages,
          advEmb,
        );
        allDocuments.push(...genericDocs);
      }

      if (artifactFiles && artifactFiles.length > 0) {
        const artifactDocs = await processFiles(
          artifactFiles,
          "designArtifact",
          folderid,
          status,
          topics,
          "imported-artifacts",
          "artifact",
          extractImages,
          advEmb,
        );
        allDocuments.push(...artifactDocs);
      }

      if (allDocuments.length > 0) {
        onDocumentsCreated(allDocuments);
      } else {
        AppToasty.show(t("toasts.noFilesToUpload"), { type: "warning" });
      }
      setIsFileUploadInProgress(false);
      return;
    }

    if (!filePath) {
      setIsFileUploadInProgress(false);
      return;
    }
    let files = [...filePath];
    const result = await processFiles(
      files,
      type,
      folderid,
      status,
      topics,
      importType,
      contentType,
      extractImages,
      advEmb,
      scope,
      source,
    );
    if (result.length > 0) {
      onDocumentsCreated(result);
    }
    setIsFileUploadInProgress(false);
  };

  const processFiles = async (
    files,
    type,
    folderid,
    status,
    topics,
    importType,
    contentType,
    extractImages = false,
    advEmb = false,
    scope = null,
    source = null,
  ) => {
    let uploadPromises = [];
    let rejectedFiles = [];

    const batchValidation = validateBatchSize(files, importType);
    if (!batchValidation.isValid) {
      AppToasty.show(batchValidation.errors.join("\n"), {
        type: "danger",
      });
      return [];
    }

    for (let file of files) {
      const sizeValidation = validateFileSize(file, importType);
      if (!sizeValidation.isValid) {
        AppToasty.show(sizeValidation.errors.join("\n"), {
          type: "danger",
        });
        rejectedFiles.push(file);
      }
    }

    if (rejectedFiles?.length > 0)
      files = files.filter((u) => !rejectedFiles.includes(u));

    for (let file of files) {
      const uploadPromise = handleImportPromise(
        file,
        type,
        folderid,
        updateUploadStatus,
        contentType,
        importType,
      );
      uploadPromises.push(uploadPromise);
    }

    if (folderid == "root") {
      folderid = "mrcRoot";
    }

    try {
      const results = await Promise.all(uploadPromises);
      const documentsPromises = [];
      for (let result of results) {
        if (result?.upload?.status == 200) {
          const ext = result?.file?.name?.split(".").pop()?.toLowerCase();
          let docType = type;
          if (allowAllTypes && !type) {
            const artifactExtensions = ["png","jpg","jpeg","gif","webp","svg","avif","mp3","m4a","aac","flac","ogg","mp4","webm","mov"];
            docType = artifactExtensions.includes(ext)
              ? "designArtifact"
              : ext === "pdf"
                ? "readComprehensionPdf"
                : "readComprehensionFile";
          }

          documentsPromises.push(
            createAPIRequest({
              data: [
                {
                  workspaceid: getActiveWorkspace()?.id,
                  userID: user?.id,
                  type: docType,
                  title: result?.file?.name
                    ?.toLowerCase()
                    ?.replace(/\.[^/.]+$/, ""),
                  folderid: folderid || "mrcRoot",
                  external_path: `s3://files-to-import-${envConfig.REACT_APP_REGION}-${envConfig.REACT_APP_ENV}/${importType}/${result?.filename}`,
                  status,
                  topics: topics?.length > 0 ? topics?.map((u) => u?.id) : null,
                  scope: scope ?? null,
                  source: source ?? null,
                },
              ],
            }),
          );
        }
      }
      const documentsUpload = await Promise.all(documentsPromises);

      if (
        documentsUpload &&
        documentsUpload?.length > 0 &&
        typeof documentsUpload[0] === "string" &&
        documentsUpload[0]?.includes(
          "You have reached the maximum number of documents",
        )
      ) {
        AppToasty.show(documentsUpload[0], {
          type: "danger",
        });
        return [];
      } else if (documentsUpload?.length === results?.length) {
        showSuccessOnUpload &&
          AppToasty.show(t("DocumentsImportUploadSuccessCaption"), {
            type: "success",
          });
        const createdDocs = documentsUpload?.map((u) => u?.data[0]);
        if (createdDocs?.length && (extractImages || advEmb)) {
          try {
            await Promise.all(
              createdDocs
                ?.filter((doc) => doc?.id)
                ?.map((doc) =>
                  updateDocumentFieldsRequest({
                    fields: {
                      id: doc?.id,
                      workspaceid: getActiveWorkspace()?.id,
                      userID: user?.id,
                      extract_images: extractImages,
                      adv_emb: advEmb,
                    },
                  }),
                ),
            );
          } catch (error) {
            console.error(
              "Failed to apply extraction/vectorisation flags on upload",
              error,
            );
          }
        }
        return createdDocs;
      } else {
        AppToasty.show(t("DocumentsImportUploadfailedCaption"), {
          type: "danger",
        });
        return [];
      }
    } catch (error) {
      console.error("An upload failed", error);
      AppToasty.show(t("DocumentsImportUploadfailedCaption"), {
        type: "danger",
      });
      return [];
    }
  };

  const updateUploadStatus = (filename, progress) => {
    // This is just an example. Replace this with your actual state update logic.
    setBatchUploadStatus((prevStatus) => ({
      ...prevStatus,
      [filename]: progress,
    }));
  };

  const filterFilesFromFolder = (files, importFile) => {
    if (!allowFolderUpload) return Array.from(files);

    const validExtensions = {
      pdf: [".pdf"],
      generic: [
        ".txt",
        ".md",
        ".html",
        ".docx",
        ".ppt",
        ".pptx",
        ".csv",
        ".xlsx",
        ".xlsm",
        ".java",
        ".py",
        ".yaml",
        ".yml",
        ".xml",
        ".sql",
        ".sh",
        ".php",
        ".js",
        ".ts",
        ".tsx",
        ".jsx",
        ".csharp",
        ".rb",
      ],
      artifact: [
        ".png",
        ".jpg",
        ".jpeg",
        ".gif",
        ".webp",
        ".svg",
        ".avif",
        ".mp3",
        ".m4a",
        ".aac",
        ".flac",
        ".ogg",
        ".mp4",
        ".webm",
        ".mov",
      ],
    };

    const extensions = validExtensions[importFile] || [];
    const allFiles = Array.from(files);

    const filteredFiles = allFiles.filter((file) => {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      return extensions.includes(ext);
    });

    return filteredFiles;
  };

  const categorizeFiles = (files) => {
    const pdfExtensions = [".pdf"];
    const genericExtensions = [
      ".txt",
      ".md",
      ".html",
      ".docx",
      ".ppt",
      ".pptx",
      ".csv",
      ".xlsx",
      ".xlsm",
      ".java",
      ".py",
      ".yaml",
      ".yml",
      ".xml",
      ".sql",
      ".sh",
      ".php",
      ".js",
      ".ts",
      ".tsx",
      ".jsx",
      ".csharp",
      ".rb",
    ];

    const artifactExtensions = [
      ".png",
      ".jpg",
      ".jpeg",
      ".gif",
      ".webp",
      ".svg",
      ".avif",
      ".mp3",
      ".m4a",
      ".aac",
      ".flac",
      ".ogg",
      ".mp4",
      ".webm",
      ".mov",
    ];

    const pdfFiles = [];
    const genericFiles = [];
    const artifactFiles = [];

    Array.from(files).forEach((file) => {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (pdfExtensions.includes(ext)) {
        pdfFiles.push(file);
      } else if (artifactExtensions.includes(ext)) {
        artifactFiles.push(file);
      } else if (genericExtensions.includes(ext)) {
        genericFiles.push(file);
      }
    });

    return { pdfFiles, genericFiles, artifactFiles };
  };

  const extractFolderName = (files) => {
    if (!allowFolderUpload || !files || files.length === 0) return null;

    const firstFile = Array.from(files)[0];
    if (firstFile && firstFile.webkitRelativePath) {
      const pathParts = firstFile.webkitRelativePath.split("/");
      return pathParts[0] || "Uploaded Folder";
    }
    return "Uploaded Folder";
  };

  const handleImportPromise = async (
    file,
    type = "readComprehensionPdf",
    folderid = "mrcRoot",
    updateUploadStatus,
    contentType = contentType,
    importType = _importType,
  ) => {
    try {
      AppToasty.show(t("legacy.DocumentImportUploadStartedCaption"), {
        type: "success",
      });
      const _appLog = await appLogHelper.createAppLog({
        status: "dispatched",
        workspaceID: state?.activeWorkspaceID,
        type: _appLogImportType,
        event: _appLogImportEvent,
        filename: file.name,
        createdBy: user?.id,
      });
      let _contentType = file.name.split(".").pop();
      const filename = `${state?.activeWorkspaceID}${flat ? "_" : "/"}${
        _appLog?.id
      }${additionalFilenameParams ? additionalFilenameParams : ""}.${
        importFile !== "generic" &&
        importFile !== "gen_ai_training" &&
        importFile !== "benchmark_ai" &&
        importFile !== "artifact"
          ? importFile?.toLowerCase()
          : _contentType
      }`;
      if (contentType === "generic") {
        // infer the content type from the file extension
        if (_contentType === "txt") {
          contentType = "text/plain";
        } else if (_contentType === "md") {
          contentType = "text/markdown";
        } else if (_contentType === "html") {
          contentType = "text/html";
        }
      }
      if (contentType === "artifact") {
        const ext = _contentType.toLowerCase();
        if (["png", "jpg", "jpeg", "gif", "webp", "avif"].includes(ext)) {
          contentType = `image/${ext}`;
        } else if (ext === "svg") {
          contentType = "image/svg+xml";
        } else if (["mp3", "m4a", "aac", "flac", "ogg"].includes(ext)) {
          contentType = `audio/${ext}`;
        } else if (["mp4", "webm", "mov"].includes(ext)) {
          contentType = `video/${ext}`;
        }
      }
      if (contentType === "gen_ai_training") {
        contentType = "application/json";
      }
      if (contentType === "benchmark_ai") {
        contentType = "text/plain";
      }

      const result = await S3uploadUrlRequest({
        filename,
        importType,
        contentType,
      });

      const upload = await axios.request({
        method: "put",
        url: JSON.parse(result?.body)?.uploadURL,
        data: file,
        headers: {
          "Content-Type": contentType,
        },
        onUploadProgress: (p) => {
          const progress = (p.loaded / p.total) * 100;
          SetFileUploadProgress(progress);
          // Update the upload status for this file.
          updateUploadStatus(file.name, progress);
        },
      });
      // Return the upload promise
      return { upload, file, filename, type, folderid };
    } catch (error) {
      AppToasty.show(t("DocumentImportUploadFailedCaption"), {
        type: "danger",
      });
      console.error("Upload result", error);
      setIsFileUploadInProgress(false);
    }
  };

  const handleImport = async ({
    file,
    importType,
    importFile,
    additionalFilenameParams,
    _appLogImportType,
    _appLogImportEvent,
    showSuccessOnUpload,
    flat,
    createDocument,
    contentType,
    title = "Imported PDF",
    folderid = "mrcRoot",
    type = "readComprehensionPdf",
  }) => {
    try {
      // Validate file size before processing
      const sizeValidation = validateFileSize(file, importType);
      if (!sizeValidation.isValid) {
        AppToasty.show(sizeValidation.errors.join("\n"), {
          type: "danger",
        });
        setIsFileUploadInProgress(false);
        return;
      }

      AppToasty.show(t("legacy.DocumentImportUploadStartedCaption"), {
        type: "success",
      });

      const _appLog = await appLogHelper.createAppLog({
        status: "dispatched",
        workspaceID: state?.activeWorkspaceID,
        type: _appLogImportType,
        event: _appLogImportEvent,
        filename: file.name,
        createdBy: user?.id,
      });
      const filename = `${state?.activeWorkspaceID}${flat ? "_" : "/"}${
        _appLog?.id
      }${
        additionalFilenameParams ? additionalFilenameParams : ""
      }.${importFile?.toLowerCase()}`;

      const result = await S3uploadUrlRequest({
        filename,
        importType,
        contentType,
      });
      const upload = await axios.request({
        method: "put",
        url: JSON.parse(result?.body)?.uploadURL,
        data: file,
        headers: {
          "Content-Type": "application/pdf",
        },

        onUploadProgress: (p) => {
          SetFileUploadProgress((p.loaded / p.total) * 100);
        },
      });
      if (JSON.parse(result?.body).uploadURL && createDocument) {
        const documentPostResult = await createAPIRequest({
          data: [
            {
              workspaceid: getActiveWorkspace()?.id,
              userID: user?.id,
              type,
              title,
              topics: [],
              folderid: folderid || "mrcRoot",
              external_path: `s3://files-to-import-${envConfig.REACT_APP_REGION}-${envConfig.REACT_APP_ENV}/imported-pdf/${filename}`,
            },
          ],
        });
        if (documentPostResult?.data) {
          showSuccessOnUpload &&
            AppToasty.show(t("DocumentImportUploadSuccessCaption"), {
              type: "success",
            });
          onDocumentCreated(documentPostResult);
        } else {
          AppToasty.show(documentPostResult, {
            type: "danger",
          });
        }
      }
    } catch (error) {
      AppToasty.show(t("DocumentImportUploadFailedCaption"), {
        type: "danger",
      });
      console.error("Upload result", error);
    } finally {
      setIsFileUploadInProgress(false);
    }
  };

  function buildFileSelector({
    importType,
    importFile,
    additionalFilenameParams,
    _appLogImportType,
    _appLogImportEvent,
    showSuccessOnUpload,
    flat,
    createDocument,
    contentType,
    uploadOnSelection,
    allowMultiple,
    allowFolderUpload,
    allowAllTypes,
  }) {
    const fileSelector = document.createElement("input");
    fileSelector.setAttribute("type", "file");
    if (allowMultiple) fileSelector.setAttribute("multiple", "");
    else fileSelector.setAttribute("single", "single");

    if (allowFolderUpload) {
      fileSelector.setAttribute("webkitdirectory", "");
      fileSelector.setAttribute("directory", "");
    }

    if (allowAllTypes) {
      fileSelector.setAttribute(
        "accept",
        ".pdf,.txt,.md,.html,.docx,.ppt,.pptx,.csv,.xlsx,.xlsm,.java,.py,.yaml,.yml,.xml,.sql,.sh,.php,.js,.ts,.tsx,.jsx,.csharp,.rb,.png,.jpg,.jpeg,.gif,.webp,.svg,.avif,.mp3,.m4a,.aac,.flac,.ogg,.mp4,.webm,.mov",
      );
    } else {
      if (importFile === "pdf") fileSelector.setAttribute("accept", ".pdf");
      if (importFile === "generic")
        fileSelector.setAttribute(
          "accept",
          ".txt,.md,.html,.docx,.ppt,.pptx,.csv,.xlsx,.xlsm,.java,.py,.yaml,.yml,.xml,.sql,.sh,.php,.js,.ts,.tsx,.jsx,.csharp,.rb",
        );
      if (importFile === "artifact")
        fileSelector.setAttribute(
          "accept",
          ".png,.jpg,.jpeg,.gif,.webp,.svg,.avif,.mp3,.m4a,.aac,.flac,.ogg,.mp4,.webm,.mov",
        );
      if (importFile === "gen_ai_training")
        fileSelector.setAttribute("accept", ".jsonl");
      if (importFile === "benchmark_ai")
        fileSelector.setAttribute("accept", ".csv");
    }
    if (uploadOnSelection && !showPreview) {
      fileSelector.addEventListener("change", function (e) {
        let files;
        if (allowAllTypes) {
          const allExtensions = [
            ".pdf",
            ".txt",
            ".md",
            ".html",
            ".docx",
            ".ppt",
            ".pptx",
            ".csv",
            ".xlsx",
            ".xlsm",
            ".java",
            ".py",
            ".yaml",
            ".yml",
            ".xml",
            ".sql",
            ".sh",
            ".php",
            ".js",
            ".ts",
            ".tsx",
            ".jsx",
            ".csharp",
            ".rb",
            ".png",
            ".jpg",
            ".jpeg",
            ".gif",
            ".webp",
            ".svg",
            ".avif",
            ".mp3",
            ".m4a",
            ".aac",
            ".flac",
            ".ogg",
            ".mp4",
            ".webm",
            ".mov",
          ];
          files = Array.from(e.target.files).filter((file) => {
            const ext = "." + file.name.split(".").pop().toLowerCase();
            return allExtensions.includes(ext);
          });
        } else {
          files = filterFilesFromFolder(e.target.files, importFile);
        }
        const folderName = allowFolderUpload
          ? extractFolderName(e.target.files)
          : null;

        if (allowFolderUpload && files.length > maxDocs) {
          files = files.slice(0, maxDocs);
          AppToasty.show(
            `Only the first ${maxDocs} documents will be uploaded from the folder.`,
            {
              type: "warning",
            },
          );
        } else if (!allowFolderUpload && files.length > maxDocs) {
          AppToasty.show(
            `You cannot upload more than ${maxDocs} documents at one time. ${files.length} files found.`,
            {
              type: "danger",
            },
          );
          fileSelector.value = "";
          return;
        }

        const batchValidation = validateBatchSize(files, importType);
        if (!batchValidation.isValid) {
          AppToasty.show(batchValidation.errors.join("\n"), {
            type: "danger",
          });
          fileSelector.value = "";
          return;
        }

        for (let file of files) {
          const sizeValidation = validateFileSize(file, importType);
          if (!sizeValidation.isValid) {
            AppToasty.show(sizeValidation.errors.join("\n"), {
              type: "danger",
            });
            fileSelector.value = "";
            return;
          }
        }

        handleImport({
          file: allowMultiple || allowFolderUpload ? files : files[0],
          importType,
          importFile,
          additionalFilenameParams,
          _appLogImportType,
          _appLogImportEvent,
          showSuccessOnUpload,
          flat,
          createDocument,
          contentType,
        });
      });
    } else {
      fileSelector.addEventListener("change", function (e) {
        processAcceptedFiles(e.target.files, { allowFolder: allowFolderUpload }, () => {
          fileSelector.value = "";
        });
      });
    }
    return fileSelector;
  }

  const processAcceptedFiles = (
    incomingFiles,
    opts = {},
    onValidationFailed = () => {},
  ) => {
    const allowFolder = opts?.allowFolder === true;
    const folderName = allowFolder ? extractFolderName(incomingFiles) : null;

    let files;
    if (allowAllTypes) {
      files = Array.from(incomingFiles).filter((file) => {
        const ext = "." + file.name.split(".").pop().toLowerCase();
        return ALL_ACCEPT_EXTENSIONS.includes(ext);
      });
    } else {
      files = filterFilesFromFolder(incomingFiles, importFile);
    }

    if (allowFolder && files.length > maxDocs) {
      files = files.slice(0, maxDocs);
      AppToasty.show(
        `Only the first ${maxDocs} documents will be uploaded from the folder.`,
        {
          type: "warning",
        },
      );
    } else if (!allowFolder && files.length > maxDocs) {
      AppToasty.show(
        `You cannot upload more than ${maxDocs} documents at one time. ${files.length} files found.`,
        {
          type: "danger",
        },
      );
      onValidationFailed();
      return;
    }

    const batchValidation = validateBatchSize(files, importType);
    if (!batchValidation.isValid) {
      AppToasty.show(batchValidation.errors.join("\n"), {
        type: "danger",
      });
      onValidationFailed();
      return;
    }

    for (let file of files) {
      const sizeValidation = validateFileSize(file, importType);
      if (!sizeValidation.isValid) {
        AppToasty.show(sizeValidation.errors.join("\n"), {
          type: "danger",
        });
        onValidationFailed();
        return;
      }
    }

    setFilePath(allowMultiple || allowFolder ? files : files[0]);
    const { pdfFiles, genericFiles, artifactFiles } = categorizeFiles(files);
    onShowPreview(
      topics,
      folderName,
      files.length,
      pdfFiles,
      genericFiles,
      artifactFiles,
    );
    return files;
  };

  const selectFiles = (files, opts = {}) => {
    if (Platform.OS !== "web" || !files || files.length === 0) return null;
    return processAcceptedFiles(files, { allowFolder: opts?.allowFolder === true }, () => {});
  };

  const removeFile = (fileName) => {
    setFilePath((prev) => {
      if (Array.isArray(prev)) {
        const next = prev.filter((f) => f?.name !== fileName);
        return next.length > 0 ? next : null;
      }
      return prev?.name === fileName ? null : prev;
    });
    setBatchUploadStatus((prev) => {
      if (prev?.[fileName] == null) return prev;
      const next = { ...prev };
      delete next[fileName];
      return next;
    });
  };
  useEffect(() => {
    if (Platform.OS === "web" && state?.activeWorkspaceID && user?.id) {
      SetFileSelector(
        buildFileSelector({
          importType,
          importFile,
          additionalFilenameParams,
          _appLogImportType,
          _appLogImportEvent,
          showSuccessOnUpload,
          flat,
          createDocument,
          contentType,
          uploadOnSelection,
          allowMultiple,
          allowFolderUpload,
          allowAllTypes,
        }),
      );
    }
  }, [
    importType,
    importFile,
    state?.activeWorkspaceID,
    user?.id,
    state?.appLogsReady,
    additionalFilenameParams,
    _appLogImportType,
    _appLogImportEvent,
    showSuccessOnUpload,
    flat,
    createDocument,
    contentType,
    uploadOnSelection,
    allowMultiple,
    allowFolderUpload,
    allowAllTypes,
    JSON.stringify(topics),
    fileSizeConstant,
  ]);
  useEffect(() => {
    setIsImportInProgress(_appLogsInImport?.length > 0);
  }, [_appLogsInImport]);
  const handleFileSelect = () => {
    if (Platform.OS === "web") {
      setBatchUploadStatus({});
      // e.preventDefault();
      fileSelector.value = null;
      fileSelector.click();
    } else {
      AppToasty.show(
        t("toasts.bulkImportNotAvailable", { platform: Platform.OS }),
        {
          type: "warning",
        },
      );
    }
  };
  useEffect(() => {
    if (filePath) onFilePathChange(filePath);
  }, [filePath]);
  return {
    handleFileSelect,
    isUploading: isImportInProgress || urlUploadGenerationInProgress,
    uploadRequestResponse: S3uploadResponse,
    isImportInProgress,
    filePath,
    importFileFromHook,
    isFileUploadInProgress,
    fileUploadProgress,
    handleImports,
    batchUploadStatus,
    selectFiles,
    removeFile,
  };
};

export default useFileSelector;
